import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Form,
  Pagination,
} from "react-bootstrap";
import theme from "../../styles/theme/theme";
import { useDispatch, useSelector } from "react-redux";
import {
  acceptBid,
  fetchMyBids,
  rejectBid,
} from "../../features/buyer/buyerSlice";
import Loader from "../../components/common/Loader";
import RejectReasonModal from "../../components/buyer/RejectReasonModal";
import CustomAlert from "../../components/common/CustomAlert";
import { timeAgo } from "../../utils/timeAgo";
import { formatCurrency, formatPrice } from "../../utils/price";
import { useSearchParams } from "react-router-dom";

const statusStyles = {
  Pending: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  Accepted: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  Rejected: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  Counter: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
};

const MyBids = () => {
  const dispatch = useDispatch();
  const { bids, bidLoading, bidError } = useSelector((state) => state.buyer);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedBidId, setSelectedBidId] = useState(null);
  const [alert, setAlert] = useState({ message: "", type: "", visible: false });
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingFilter, setLoadingFilter] = useState(false);

  const PAGE_SIZE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const statusFromUrl = searchParams.get("status");

    if (statusFromUrl) {
      setStatusFilter(statusFromUrl.toLowerCase());
      setCurrentPage(1);
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchMyBids()).catch((err) => {
      console.error("Failed to load bids:", err);
    });
  }, [dispatch]);

  const handleAccept = async (id) => {
    try {
      const response = await dispatch(acceptBid(id)).unwrap();
      dispatch(fetchMyBids());
      setAlert({
        message: response.message || "Bid accepted successfully!",
        type: "success",
        visible: true,
      });
    } catch (err) {
      setAlert({
        message: err?.message || "Failed to accept bid.",
        type: "error",
        visible: true,
      });
    }
  };

  const handleRejectClick = (id) => {
    setSelectedBidId(id);
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async (reason) => {
    if (selectedBidId) {
      try {
        const response = await dispatch(
          rejectBid({ id: selectedBidId, reason })
        ).unwrap();
        dispatch(fetchMyBids());
        setAlert({
          message: response.data || "Bid rejected successfully!",
          type: "error",
          visible: true,
        });
      } catch (err) {
        setAlert({
          message: err.data || "Failed to reject bid.",
          type: "error",
          visible: true,
        });
      }
    }
    setShowRejectModal(false);
    setSelectedBidId(null);
  };

  const handleRejectModalClose = () => {
    setShowRejectModal(false);
    setSelectedBidId(null);
  };

  //  Filter first
  const filteredBids = bids.filter((bid) =>
    statusFilter === "all"
      ? true
      : bid.status.toLowerCase() === statusFilter.toLowerCase()
  );

  //  Then paginate
  const totalPages = Math.ceil(filteredBids.length / PAGE_SIZE);
  const paginatedBids = filteredBids.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  //  Pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  if (bidLoading) return <Loader />;
  if (bidError) return <Alert variant="danger">{bidError}</Alert>;

  return (
    <Container className="py-4 px-3 px-md-5">
      <div className="d-flex flex-nowrap justify-content-between align-items-center mb-4">
        <h3
          style={{
            color: theme.palette.text.primary,
            marginBottom: 0,
            fontSize: "1.2rem",
          }}
        >
          My Bids
        </h3>
        <Form.Select
          value={statusFilter}
          onChange={(e) => {
            setLoadingFilter(true);
            setStatusFilter(e.target.value);
            setCurrentPage(1); //  reset to page 1 on filter change
            setTimeout(() => setLoadingFilter(false), 300);
          }}
          style={{
            maxWidth: 150,
            flexShrink: 0,
          }}
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="countered">Countered</option>
        </Form.Select>
      </div>

      {loadingFilter ? (
        <Loader />
      ) : filteredBids.length === 0 ? (
        <div
          className="text-center py-5"
          style={{ color: theme.palette.text.secondary }}
        >
          No bids found for this filter.
        </div>
      ) : (
        <>
          <Row className="gy-4">
            {paginatedBids.map((bid) => {
              const statusKey =
                bid.status.charAt(0).toUpperCase() + bid.status.slice(1);
              return (
                <Col key={bid.id} xs={12} sm={6} md={3} lg={3}>
                  <div
                    style={{
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: theme.shape.borderRadius,
                      boxShadow: theme.shadows[2],
                      padding: "14px 16px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      height: "100%",
                    }}
                  >
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5
                          className="mb-0"
                          style={{
                            color: theme.palette.text.primary,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                          }}
                        >
                          {bid.product?.name}
                        </h5>
                        <span
                          className="badge"
                          style={{
                            ...statusStyles[statusKey],
                            padding: "5px 12px",
                            borderRadius: "20px",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                        >
                          {statusKey}
                        </span>
                      </div>

                      <div style={{ fontSize: "0.95rem", marginBottom: 8 }}>
                        <strong style={{ color: theme.palette.text.primary }}>
                          Bid Price:
                        </strong>{" "}
                        {/* A${formatPrice(bid.bid_price)} */}
                        {formatCurrency(bid.bid_price, bid.currency)}
                      </div>

                      {bid.status === "countered" && bid.counter_price && (
                        <div style={{ fontSize: "0.95rem", marginBottom: 8 }}>
                          <strong style={{ color: theme.palette.text.primary }}>
                            Counter Offer:
                          </strong>{" "}
                          <strong>
                            {/* A${formatPrice(bid.counter_price)} */}
                            {formatCurrency(bid.counter_price, bid.currency)}
                          </strong>
                        </div>
                      )}

                      <div style={{ fontSize: "0.95rem", marginBottom: 8 }}>
                        <strong style={{ color: theme.palette.text.primary }}>
                          Date:
                        </strong>{" "}
                        {timeAgo(bid.created_at)}
                      </div>

                      {bid.status === "rejected" && bid.rejection_reason && (
                        <div style={{ fontSize: "0.95rem" }}>
                          <strong style={{ color: theme.palette.text.primary }}>
                            Reason:
                          </strong>{" "}
                          <strong>{bid.rejection_reason}</strong>
                        </div>
                      )}
                    </div>

                    {bid.status === "countered" && (
                      <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                          variant="outline-danger"
                          size="md"
                          style={{
                            borderColor: theme.palette.error.main,
                            color: theme.palette.error.main,
                            padding: "4px 12px",
                            fontSize: "0.85rem",
                          }}
                          onClick={() => handleRejectClick(bid.id)}
                        >
                          Reject
                        </Button>
                        <Button
                          size="md"
                          style={{
                            backgroundColor: theme.palette.primary.main,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            padding: "4px 14px",
                            fontSize: "0.85rem",
                          }}
                          onClick={() => handleAccept(bid.id)}
                        >
                          Accept
                        </Button>
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>

          {totalPages > 1 && (
            <>
              <Pagination className="mt-4 justify-content-center custom-pagination">
                <Pagination.Prev
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                />
                {paginationItems}
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                />
              </Pagination>
              <div
                className="text-center mt-2"
                style={{ color: theme.palette.text.secondary }}
              >
                Page {currentPage} of {totalPages}
              </div>
            </>
          )}
        </>
      )}

      {/* Reject Reason Modal */}
      <RejectReasonModal
        show={showRejectModal}
        onHide={handleRejectModalClose}
        onSubmit={handleRejectSubmit}
      />
      {alert.visible && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ ...alert, visible: false })}
        />
      )}
    </Container>
  );
};

export default MyBids;
